import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import PositionsTable from '../components/PositionsAdmin/PositionsTable';
import FilterPanel from '../components/PositionsAdmin/FilterPanel';
import SearchBar from '../components/PositionsAdmin/SearchBar';
import { CheckCircle, Pause, Play, Square } from 'lucide-react';

const PositionsAdmin = () => {
  const { userRole, isLoggedIn, loading } = useContext(AuthContext);
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    fields: [],
    country: '',
    prestigeMin: '',
    prestigeMax: '',
    atRisk: '',
    reported: '',
    linkStatus: '',
    createdBy: ''
  });
  const [isValidatingLinks, setIsValidatingLinks] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [validationProgress, setValidationProgress] = useState({ current: 0, total: 0 });
  const [currentJobId, setCurrentJobId] = useState(null);
  const [isStopped, setIsStopped] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Extract unique fields from positions data
  const uniqueFields = useMemo(() => {
    const fieldsSet = new Set();
    positions.forEach(position => {
      if (position.fields && Array.isArray(position.fields)) {
        position.fields.forEach(field => fieldsSet.add(field));
      }
    });
    return Array.from(fieldsSet).sort();
  }, [positions]);

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoadingPositions(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/all`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setPositions(response.data);
      setFilteredPositions(response.data);
    } catch (error) {
      console.error('Error fetching positions:', error);
      alert('Failed to fetch positions');
    } finally {
      setLoadingPositions(false);
    }
  };

  const handleDeletePosition = async (positionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${positionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Remove from positions
      setPositions(prevPositions => prevPositions.filter(p => p._id !== positionId));
      setFilteredPositions(prevPositions => prevPositions.filter(p => p._id !== positionId));
      
      alert('Position deleted successfully');
    } catch (error) {
      console.error('Error deleting position:', error);
      alert(error.response?.data?.message || 'Failed to delete position');
    }
  };

  const handleValidateLinks = async () => {
    try {
      setIsValidatingLinks(true);
      const totalPositions = positions.filter(p => p.link).length;
      setValidationProgress({ current: 0, total: totalPositions });
      
      const token = localStorage.getItem('token');
      
      // Start validation job
      const startResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/validate-links`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      const jobId = startResponse.data.jobId;
      setCurrentJobId(jobId);
      
      // Poll for progress
      let pollInterval;
      const pollForProgress = () => {
        pollInterval = setInterval(async () => {
          try {
            const progressResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/positions/validate-links/status/${jobId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );
            
            const progress = progressResponse.data;
            setValidationProgress({ 
              current: progress.checked, 
              total: progress.total 
            });
            
            if (progress.status === 'complete' || progress.status === 'error' || progress.status === 'stopped') {
              clearInterval(pollInterval);
              
              // Refresh positions after validation
              await fetchPositions();
              
              if (progress.status === 'stopped') {
                alert(
                  `Validation Stopped!\n` +
                  `Checked: ${progress.checked} of ${progress.total}\n` +
                  `Valid: ${progress.valid}\n` +
                  `Invalid: ${progress.invalid}`
                );
                setIsStopped(true);
              } else if (progress.status === 'complete') {
                alert(
                  `Validation Complete!\n` +
                  `Total: ${progress.total}\n` +
                  `Valid: ${progress.valid}\n` +
                  `Invalid: ${progress.invalid}`
                );
              } else {
                alert(`Validation failed: ${progress.error}`);
              }
              
              setIsValidatingLinks(false);
              setValidationProgress({ current: 0, total: 0 });
              setIsPaused(false);
              setCurrentJobId(null);
            }
          } catch (error) {
            console.error('Error polling progress:', error);
          }
        }, 1000); // Poll every second
      };
      
      pollForProgress();
      
      // Store interval to allow pause/resume
      window.validationPollInterval = pollInterval;
      
      // Clean up if component unmounts
      return () => {
        if (pollInterval) clearInterval(pollInterval);
      };
      
    } catch (error) {
      console.error('Error starting validation:', error);
      alert('Failed to start validation');
      setIsValidatingLinks(false);
      setValidationProgress({ current: 0, total: 0 });
    }
  };

  const handlePauseResume = async () => {
    if (!currentJobId) return;
    
    try {
      const token = localStorage.getItem('token');
      
      if (isPaused) {
        // Resume
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/validate-links/${currentJobId}/resume`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        setIsPaused(false);
      } else {
        // Pause
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/validate-links/${currentJobId}/pause`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        setIsPaused(true);
        if (window.validationPollInterval) {
          clearInterval(window.validationPollInterval);
        }
      }
    } catch (error) {
      console.error('Error pausing/resuming:', error);
      alert('Failed to pause/resume validation');
    }
  };

  const handleStop = async () => {
    if (!currentJobId) return;
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/validate-links/${currentJobId}/stop`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Stop polling
      if (window.validationPollInterval) {
        clearInterval(window.validationPollInterval);
      }
      
      setIsValidatingLinks(false);
      setIsPaused(false);
      setCurrentJobId(null);
      
      // Refresh positions to show current status
      await fetchPositions();
    } catch (error) {
      console.error('Error stopping validation:', error);
      alert('Failed to stop validation');
    }
  };

  // Helper function to check if position is at risk (over 1 month old)
  const isAtRisk = (createdAt) => {
    if (!createdAt) return false;
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return new Date(createdAt) < oneMonthAgo;
  };

  // Apply search and filters
  useEffect(() => {
    let filtered = [...positions];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(position =>
        position.title?.toLowerCase().includes(query) ||
        position.institution?.toLowerCase().includes(query) ||
        position.description?.toLowerCase().includes(query)
      );
    }

    // Apply field filters
    if (filters.fields.length > 0) {
      filtered = filtered.filter(position =>
        position.fields?.some(field => filters.fields.includes(field))
      );
    }

    // Apply country filter
    if (filters.country) {
      filtered = filtered.filter(position =>
        position.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    // Apply prestige score filters
    if (filters.prestigeMin !== '') {
      filtered = filtered.filter(position => 
        position.prestige_score >= parseFloat(filters.prestigeMin)
      );
    }
    if (filters.prestigeMax !== '') {
      filtered = filtered.filter(position => 
        position.prestige_score <= parseFloat(filters.prestigeMax)
      );
    }

    // Apply at risk filter
    if (filters.atRisk !== '') {
      filtered = filtered.filter(position => {
        const atRisk = isAtRisk(position.createdAt);
        return filters.atRisk === 'atRisk' ? atRisk : !atRisk;
      });
    }

    // Apply reported filter
    if (filters.reported !== '') {
      filtered = filtered.filter(position => {
        const reported = position.reportedBy && position.reportedBy.length > 0;
        return filters.reported === 'reported' ? reported : !reported;
      });
    }

    // Apply link status filter
    if (filters.linkStatus !== '') {
      filtered = filtered.filter(position => {
        return position.linkStatus === filters.linkStatus;
      });
    }

    // Apply createdBy filter
    if (filters.createdBy !== '') {
      filtered = filtered.filter(position => {
        return position.createdBy === filters.createdBy;
      });
    }

    setFilteredPositions(filtered);
  }, [positions, searchQuery, filters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredPositions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPositions = filteredPositions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  // Apply sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });

    const sorted = [...filteredPositions].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle dates
      if (key === 'createdAt') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle undefined/null values
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPositions(sorted);
  };

  if (loading || loadingPositions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isLoggedIn || userRole !== 'admin') {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
        <p>Only administrators can access this page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '100%' }}>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Positions Management
          </h1>
          <p className="text-gray-600">
            View and manage all positions in the system
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Action Button */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleValidateLinks}
                  disabled={isValidatingLinks || loadingPositions}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={20} />
                  <span>{isValidatingLinks ? 'Validating Links...' : 'Validate All Links'}</span>
                </button>
                {isValidatingLinks && (
                  <>
                    <button
                      onClick={handlePauseResume}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      {isPaused ? (
                        <>
                          <Play size={20} />
                          <span>Resume</span>
                        </>
                      ) : (
                        <>
                          <Pause size={20} />
                          <span>Pause</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleStop}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Square size={18} />
                      <span>End</span>
                    </button>
                  </>
                )}
              </div>
              {isValidatingLinks && (
                <div className="text-sm text-gray-600">
                  Checking {validationProgress.current}/{validationProgress.total} positions...
                </div>
              )}
            </div>
            {isValidatingLinks && validationProgress.total > 0 && (
              <div className="w-full">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Progress</span>
                  <span className="text-xs font-medium text-gray-700">
                    {Math.round((validationProgress.current / validationProgress.total) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{
                      width: `${(validationProgress.current / validationProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Search and Filter Controls */}
          <div className="mb-6 space-y-4">
            <SearchBar 
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <FilterPanel 
              filters={filters}
              onFiltersChange={setFilters}
              uniqueFields={uniqueFields}
            />
          </div>

          {/* Stats */}
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredPositions.length)} of {filteredPositions.length} positions
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>

          {/* Positions Table */}
          <PositionsTable 
            positions={paginatedPositions}
            onSort={handleSort}
            sortConfig={sortConfig}
            onDelete={handleDeletePosition}
          />

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionsAdmin;
